import { union, values, intersection } from 'underscore'

import {
  getFieldsProperties,
  getContentLabelFields,
  getI18nProperties
} from '@/store/utils/labels'
import cardFormModule from '@/store/modules/namespace/cardForm'
import DefaultBrandOrder from '@/configuration/sources/DefaultBrandOrder.yml'
import DebitCardList from '@/configuration/sources/DebitCardList.yml'

const mapCardFormActions = actionNames => {
  const map = {}
  for (const name of actionNames) {
    map[name] = ({ dispatch, state }, params = {}) => {
      const { args, _form = null } = params
      const label = state.activeForm
      const formId = _form ?? state.forms[label]
      if (formId) {
        dispatch(`cardForm_${formId}/${name}`, args)
      }
    }
  }
  return map
}

const mapCardFormGetters = getterNames => {
  const map = {}
  for (const name of getterNames) {
    map[name] = (state, getters) => {
      const label = state.activeForm
      const formId = state.forms[label]
      return getters[`cardForm_${formId}/${name}`]
    }
  }
  return map
}

export const getFormState = () => {
  return {
    formWidget: 'default', // or 'smartForm'
    formType: 'embedded', // or 'popin'
    formMode: 'default', // or 'token|wallet'
    forms: {}, // default: <ID>, modal: <ID>, ILLICADO: <ID>, OG_<BRAND>: <ID>
    cardFormBlueprint: null,
    activeForm: 'default', // or 'modal' | 'ILLICADO' | OG_<BRAND>: <ID>
    renderedCardForm: [],
    disabledForm: false, // Global, include smartform methods and card forms
    hasDynamicValues: false,
    placeholders: {
      ...getFieldsProperties(getI18nProperties(null), true)
    },
    labels: {
      ...getFieldsProperties(getI18nProperties(null), false)
    },
    contentLabels: {
      ...getContentLabelFields(getI18nProperties(null))
    },
    postUrlSuccess: null,
    postUrlRefused: null,
    getUrlSuccess: null,
    getUrlRefused: null,
    payBtnHTML: '',
    clearOnError: null,
    flexMode: false,
    spaMode: false,
    formPresets: [],
    readyForms: [],
    // Forms that have had their embedded iframes made visible
    // Reactive so any future patch/re-render of KryptonField
    // reflects the real visibility instead of a stale one-time snapshot.
    // see KJS-5601
    visibleForms: []
  }
}

export const formActions = ($locator, app) => {
  return {
    ...mapCardFormActions([
      'disableField',
      'enableField',
      'hideField',
      'showField',
      'unsetInputFilled',
      'setInputFilled',
      'validateField',
      'invalidateField',
      'viableField',
      'inviableField',
      'forceFieldClear',
      'forceFieldBlur',
      'fieldClear',
      'resetBrand',
      'updateNonSensitiveValue',
      'startWalletPayment',
      'closeWalletPayment',
      'setWalletMode',
      'resetBinOptions',
      'resetForm',
      'deleteDNAToken',
      'selectBrand',
      'setPaymentBrand',
      'disableCardForm',
      'enableCardForm',
      'setTestCard',
      'setBrandFallback',
      'toggleSelectField'
    ]),
    updateActiveForm({ dispatch, state }, params) {
      const formId = state.forms[state.activeForm]
      dispatch(`cardForm_${formId}/update`, params)
    },
    addFormPreset({ dispatch, commit }, preset) {
      commit('ADD_FORM_PRESET', preset)
      dispatch('forEachForm', [preset.action, preset.params])
    },
    forEachForm({ dispatch, state }, [action, params]) {
      for (const label in state.forms) {
        const id = state.forms[label]
        dispatch(`cardForm_${id}/${action}`, params)
      }
    },
    registerCardFormModule({ state, dispatch }, { id }) {
      const { $store } = $locator

      if ($store.hasModule([`cardForm_${id}`])) return
      $store.registerModule(
        [`cardForm_${id}`],
        cardFormModule($locator, app, id)
      )
      state.formPresets.forEach(({ action, params }) => {
        dispatch(`cardForm_${id}/${action}`, params)
      })
    },
    unregisterCardFormModule(ctx, { id }) {
      const { $store } = $locator

      if (!$store.hasModule([`cardForm_${id}`])) return
      $store.unregisterModule([`cardForm_${id}`])
    },
    addForm({ commit }, { label, id }) {
      commit('UPDATE', {
        forms: {
          [label]: id
        }
      })
    },
    resetForms({ state, dispatch, commit }) {
      if (app === 'host') {
        for (const label in state.forms) {
          const id = state.forms[label]
          dispatch('unregisterCardFormModule', { id })
        }
        commit('RESET_FORM_PRESET')
        commit('RESET_FORM_LIST')
      }
    },
    renderCardForm({ state, commit }, id) {
      // Using UPDATE because custom mutation did not work in specific case see KJS-5035
      commit('UPDATE', { renderedCardForm: [...state.renderedCardForm, id] })
    },
    focusForm({ state, commit }, label) {
      if (!(label in state.forms)) {
        console.warn(`Cannot focus unregistered form "${label}"`)
      }

      commit('UPDATE', {
        activeForm: label
      })
    },
    enableButton({ commit }) {
      commit('UPDATE', { button: { disabled: false } })
    },
    disableButton({ commit }) {
      commit('UPDATE', { button: { disabled: true } })
    },
    /**
     * Careful with the name: this enables/disables the whole form
     * (card forms, and payment methods included)
     */
    disableForm({ commit, dispatch }) {
      commit('UPDATE', { disabledForm: true })
      dispatch('forEachForm', ['disableCardForm'])
    },
    enableForm({ commit, dispatch }) {
      dispatch('forEachForm', ['enableCardForm'])
      commit('UPDATE', { disabledForm: false })
    }
  }
}

export const formMutations = {
  ADD_FORM_PRESET: (state, preset) => {
    state.formPresets.push(preset)
  },
  RESET_FORM_LIST: state => {
    state.forms = {}
  },
  RESET_FORM_PRESET: state => {
    state.formPresets.length = 0
  }
}
export const formGetters = {
  ...mapCardFormGetters([
    'isFormFilled',
    'isFieldDisabled',
    'isFieldVisible',
    'isFieldInvalid',
    'isFieldViable',
    'isWalletMyCards',
    'isTokenActive',
    'isAnyTokenActive'
  ]),
  getFromEachFormState: state => formStatePath => {
    return Object.values(state.forms).map(formId => {
      return {
        formId,
        value: formStatePath
          .split('.')
          .reduce((acc, key) => acc?.[key], state[`cardForm_${formId}`])
      }
    })
  },
  getActiveCardFormState: state => {
    const { forms, activeForm } = state
    return state[`cardForm_${forms[activeForm]}`]
  },
  getActiveCardFormGetter: (state, getters) => getterName => {
    const { forms, activeForm } = state
    return getters[`cardForm_${forms[activeForm]}/${getterName}`]
  },
  isCardFormVisible:
    (
      { formType, formWidget, redirectIframeOpen, activeForm, forms },
      {
        cardsFormExpanded,
        hasCardMethodAvailable,
        isExtrasFormVisible,
        isCardBrandAsMethodForm
      }
    ) =>
    formId => {
      return (
        ((formType === 'embedded' && formWidget === 'default') ||
          !redirectIframeOpen ||
          formId !== forms[activeForm] ||
          (formWidget === 'smartForm' && cardsFormExpanded)) &&
        (hasCardMethodAvailable ||
          isExtrasFormVisible ||
          isCardBrandAsMethodForm(formId)) //form inside button, button controls the visibility
      )
    },
  isFormReady: state => {
    const { forms, readyForms } = state
    return label => {
      return !!(forms[label] && readyForms.includes(forms[label]))
    }
  },
  isBrandAvailable: (state, getters) => brand =>
    !!~getters.availableBrands.indexOf(brand),
  isDebitBrand: state => brand => {
    return !!~DebitCardList.brands.indexOf(brand)
  },
  availableBrands: state =>
    state?.dna?.cards ? Object.keys(state.dna.cards) : [],
  isAccountFundsTransfer: state => state?.dna?.serviceType === 'FUNDS_TRANSFER',
  brandSorter:
    ({ dna }) =>
    (brandA, brandB) => {
      const brandOrder = union(
        values(dna?.brandPriority || {}),
        DefaultBrandOrder
      )
      const indexA = brandOrder?.indexOf(brandA)
      const indexB = brandOrder?.indexOf(brandB)
      if (~indexA && !~indexB) return -1
      else if (!~indexA && ~indexB) return 1
      return indexA - indexB
    },
  brandsWithPriority:
    ({ dna }) =>
    (brands = []) => {
      const brandPriority = dna?.brandPriority
      return intersection(brands, brandPriority).length === brands.length
    },
  isAnyCardFormDisabled: state => {
    return Object.values(state.forms).some(
      id => state[`cardForm_${id}`].disabledCardForm
    )
  },
  activeFormId: ({ activeForm, forms }) => forms[activeForm],
  isAdditionalForm: ({ forms }) => {
    return formId => formId.length > 0 && formId !== forms.default
  },
  isCardFormRendered: ({ renderedCardForm }) => {
    return formId => {
      return renderedCardForm.includes(formId)
    }
  },
  /**
   * Return payBtnHtml innerText value in order to set button title for
   * ARIA compliance.
   *
   * @returns {string}
   * @since KJS-4814
   */
  payBtnText: ({ payBtnHTML }) => {
    const domParser = new DOMParser()
    const document = domParser.parseFromString(payBtnHTML, 'text/html')
    return document.body.textContent?.trim()
  },
  relatedForm({ forms }) {
    return formId => {
      const relatedForm = Object.entries(forms || {}).find(
        ([, associatedId]) => formId === associatedId
      )?.[0]
      return relatedForm
    }
  },
  isNewCardBrandSelector({ dna, country, _internals }) {
    if (_internals.forcedCardBrandSelector)
      return _internals.forcedCardBrandSelector == 'new'

    return dna?.newBrandChoice
  }
}
